import { clearPortalToken } from "@/domains/auth/lib/portalToken";
import { areWatchFeaturesLocked, isToolboxPlan, isTrialExpired } from "@/domains/billing/lib/planAccess";
import { apiRequest, isAuthError } from "@/shared/lib/apiClient";
import { captureException, clearOTelUser, setOTelUser } from "@/shared/lib/otel";
import { queryKeys } from "@/shared/lib/queryKeys";
import { resolveTimezone, setTimeFormat, setTimezone } from "@/shared/lib/timezone";
import type { AlertEvent, ApiFn, Entitlements, Profile, UserPrefs, WatchRule } from "@/shared/lib/types";
import {
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";


interface WavesOverviewCache {
  dates: string[];
  summaryByKey: Record<string, unknown>;
  intradayByKey: Record<string, unknown>;
}
interface AppContextType {
  profile: Profile | null;
  entitlements: Entitlements | null;
  loading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  watches: WatchRule[];
  watchesLoading: boolean;
  setWatches: Dispatch<SetStateAction<WatchRule[]>>;
  alerts: AlertEvent[];
  alertsLoading: boolean;
  prefs: UserPrefs | null;
  setPrefs: Dispatch<SetStateAction<UserPrefs | null>>;
  addToast: (msg: string, type?: "success" | "error" | "info") => void;
  updateProfilePlan: (plan: string) => void;
  loadProfile: (silent?: boolean) => Promise<void>;
  loadWatches: () => Promise<void>;
  api: ApiFn;
  logout: () => Promise<void>;
  liveOptions: unknown[];
  setLiveOptions: Dispatch<SetStateAction<unknown[]>>;
  liveOptionsFetched: boolean;
  setLiveOptionsFetched: Dispatch<SetStateAction<boolean>>;
  zdteOptions: unknown[];
  setZdteOptions: Dispatch<SetStateAction<unknown[]>>;
  zdteOptionsFetched: boolean;
  setZdteOptionsFetched: Dispatch<SetStateAction<boolean>>;
  wavesOverview: WavesOverviewCache;
  setWavesOverview: Dispatch<SetStateAction<WavesOverviewCache>>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

type WatchesResponse = { watches?: WatchRule[] } | null;
type HistoryResponse = { events?: AlertEvent[] } | null;

function resolveSetState<T>(current: T, action: SetStateAction<T>): T {
  return typeof action === "function" ? (action as (previous: T) => T)(current) : action;
}

function getWatchSignature(watch: WatchRule) {
  return `${watch.alerts_sent ?? 0}|${watch.updated_at ?? ""}|${watch.status ?? ""}`;
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [trialExpiredNotified, setTrialExpiredNotified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveOptions, setLiveOptions] = useState<unknown[]>([]);
  const [liveOptionsFetched, setLiveOptionsFetched] = useState(false);
  const [zdteOptions, setZdteOptions] = useState<unknown[]>([]);
  const [zdteOptionsFetched, setZdteOptionsFetched] = useState(false);
  const [wavesOverview, setWavesOverview] = useState<WavesOverviewCache>({
    dates: [],
    summaryByKey: {},
    intradayByKey: {},
  });

  const addToast = useCallback(
    (msg: string, type: "success" | "error" | "info" = "info") => {
      if (type === "success") toast.success(msg);
      else if (type === "error") toast.error(msg);
      else toast.info(msg);
    },
    [],
  );

  const meQuery = useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) =>
      apiRequest<{ profile: Profile; entitlements: Entitlements }>("GET", "/api/user/me", { signal }),
    staleTime: 5 * 60_000,
  });

  const profile = meQuery.data?.profile ?? null;
  const isAuthenticated = Boolean(profile) && !isAuthError(meQuery.error);
  const watchFeaturesLocked = areWatchFeaturesLocked(profile);

  const watchesQuery = useQuery({
    queryKey: queryKeys.watches(),
    queryFn: async ({ signal }) => {
      const data = await apiRequest<WatchesResponse>("GET", "/api/watches", { signal });
      return data?.watches ?? [];
    },
    enabled: isAuthenticated && !watchFeaturesLocked,
    staleTime: 30_000,
  });

  const prefsQuery = useQuery({
    queryKey: queryKeys.notificationPrefs(),
    queryFn: ({ signal }) => apiRequest<UserPrefs | null>("GET", "/api/notifications/preferences", { signal }),
    enabled: isAuthenticated && !watchFeaturesLocked,
    staleTime: 5 * 60_000,
  });

  const watches = watchesQuery.data ?? [];
  const watchedWithAlerts = useMemo(
    () => watches.filter((watch) => (watch.alerts_sent ?? 0) > 0 && watch.watch_id),
    [watches],
  );

  const historyQueries = useQueries({
    queries: watchedWithAlerts.map((watch) => {
      const watchId = String(watch.watch_id);
      return {
        queryKey: [...queryKeys.watchHistory(watchId), getWatchSignature(watch)],
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
          apiRequest<HistoryResponse>("GET", `/api/watches/${watchId}/history`, { signal }),
        enabled: isAuthenticated && !watchFeaturesLocked,
        staleTime: 30_000,
        retry: false,
      };
    }),
  });

  const alerts = useMemo(() => {
    const allAlerts: AlertEvent[] = [];

    historyQueries.forEach((query, index) => {
      if (!query.data?.events) return;
      const watch = watchedWithAlerts[index];
      if (!watch) return;
      const watchId = String(watch.watch_id || "");

      allAlerts.push(
        ...query.data.events.map((event) => ({
          ...event,
          ticker: watch.ticker,
          option_contract: watch.option_contract,
          intent: watch.intent,
          watch_id: watchId,
        })),
      );
    });

    allAlerts.sort((a, b) => Number(b.timestamp ?? 0) - Number(a.timestamp ?? 0));
    return allAlerts.slice(0, 10);
  }, [historyQueries, watchedWithAlerts]);

  const alertsLoading = historyQueries.some((query) => query.isPending || query.isFetching);

  const invalidateForMutation = useCallback(
    async (method: string, path: string) => {
      if (method.toUpperCase() === "GET") return;

      const invalidations: Promise<unknown>[] = [];
      if (path.includes("/api/watches") && !path.includes("/preview")) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: queryKeys.watches() }));
      }
      if (path.includes("/api/notifications")) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: queryKeys.notificationsRoot() }));
      }
      if (path.includes("/api/user") || path.includes("/api/billing") || path.includes("/api/checkout")) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: queryKeys.me() }));
      }

      await Promise.all(invalidations);
    },
    [queryClient],
  );

  const api = useCallback(
    async <TResponse = unknown>(
      method: string,
      path: string,
      body?: unknown,
      options?: { silent?: boolean; signal?: AbortSignal },
    ): Promise<TResponse> => {
      try {
        const result = await apiRequest<TResponse>(method, path, {
          body,
          silent: options?.silent,
          signal: options?.signal,
        });
        await invalidateForMutation(method, path);
        return result;
      } catch (err) {
        if (isAuthError(err)) {
          queryClient.setQueryData(queryKeys.profile(), null);
          if (!options?.silent && window.location.pathname !== "/login") {
            navigate("/login");
          }
        }
        throw err;
      }
    },
    [invalidateForMutation, navigate, queryClient],
  );

  const setWatches = useCallback<Dispatch<SetStateAction<WatchRule[]>>>(
    (action) => {
      queryClient.setQueryData<WatchRule[]>(queryKeys.watches(), (current = []) =>
        resolveSetState(current, action),
      );
    },
    [queryClient],
  );

  const setPrefs = useCallback<Dispatch<SetStateAction<UserPrefs | null>>>(
    (action) => {
      queryClient.setQueryData<UserPrefs | null>(queryKeys.notificationPrefs(), (current = null) =>
        resolveSetState(current, action),
      );
    },
    [queryClient],
  );

  const updateProfilePlan = useCallback(
    (plan: string) => {
      queryClient.setQueryData<{ profile: Profile; entitlements: Entitlements } | undefined>(
        queryKeys.me(),
        (current) => {
          if (!current) return current;
          return {
            ...current,
            profile: { ...current.profile, plan },
            entitlements: { ...current.entitlements, plan },
          };
        },
      );

      if (plan.toLowerCase() === "toolbox") {
        setWatches([]);
        queryClient.setQueryData(queryKeys.notificationPrefs(), null);
      }
    },
    [queryClient, setWatches],
  );

  const loadWatches = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.watches() });
  }, [queryClient]);

  const loadProfile = useCallback(
    async () => {
      setError(null);
      await meQuery.refetch();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.watches() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.notificationPrefs() }),
      ]);
    },
    [meQuery, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await apiRequest("POST", "/api/user/logout");
    } catch {
      // Logout is best-effort; local state is cleared regardless.
    } finally {
      clearOTelUser();
      clearPortalToken();
      queryClient.clear();
      setLiveOptions([]);
      setLiveOptionsFetched(false);
      setZdteOptions([]);
      setZdteOptionsFetched(false);
      setWavesOverview({ dates: [], summaryByKey: {}, intradayByKey: {} });
      navigate("/");
    }
  }, [navigate, queryClient]);

  useEffect(() => {
    if (!meQuery.error) return;
    if (isAuthError(meQuery.error)) {
      clearOTelUser();
      if (window.location.pathname.startsWith("/portal")) {
        navigate("/login");
      }
      return;
    }

    setError("Failed to load profile");
    captureException(
      meQuery.error instanceof Error ? meQuery.error : new Error(String(meQuery.error)),
      { context: "meQuery" },
    );
  }, [navigate, meQuery.error]);

  useEffect(() => {
    const queryErrors = [
      { error: watchesQuery.error, context: "watchesQuery" },
      { error: prefsQuery.error, context: "prefsQuery" },
    ];

    queryErrors.forEach(({ error, context }) => {
      if (!error || isAuthError(error)) return;
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        { context },
      );
    });
  }, [prefsQuery.error, watchesQuery.error]);

  useEffect(() => {
    if (profile) {
      setOTelUser({ id: profile.user_id ?? profile.id ?? "unknown" });
      const effectiveTz = resolveTimezone(profile.timezone_mode ?? null, profile.timezone ?? null);
      setTimezone(effectiveTz);
      setTimeFormat(profile.time_format ?? "24h");
      return;
    }
    clearOTelUser();
  }, [profile]);

  useEffect(() => {
    if (!profile || !isTrialExpired(profile) || trialExpiredNotified) return;
    addToast(
      "Your trial has ended. Upgrade now to continue using the dashboard and watches.",
      "error",
    );
    setTrialExpiredNotified(true);
  }, [addToast, profile, trialExpiredNotified]);

  useEffect(() => {
    if (profile && isToolboxPlan(profile)) {
      queryClient.setQueryData(queryKeys.watches(), []);
      queryClient.setQueryData(queryKeys.notificationPrefs(), null);
    }
  }, [profile, queryClient]);


  return (
    <AppContext.Provider
      value={{
        profile,
        entitlements: meQuery.data?.entitlements ?? null,
        watches: watchFeaturesLocked ? [] : watches,
        watchesLoading: watchesQuery.isPending,
        setWatches,
        alerts: watchFeaturesLocked ? [] : alerts,
        alertsLoading,
        prefs: watchFeaturesLocked ? null : prefsQuery.data ?? null,
        setPrefs,
        loading: meQuery.isPending,
        isAuthenticated,
        error,
        addToast,
        updateProfilePlan,
        loadProfile,
        loadWatches,
        api,
        logout,
        liveOptions,
        setLiveOptions,
        liveOptionsFetched,
        setLiveOptionsFetched,
        zdteOptions,
        setZdteOptions,
        zdteOptionsFetched,
        setZdteOptionsFetched,
        wavesOverview,
        setWavesOverview,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
